import React, { useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { interestTooltipLabel, splitInterestEmojiGlyphs } from '../utils/interestUtils';

const DEFAULT_MAX_VISIBLE = 3;

const SIZE_PRESETS = {
  default: {
    emojiSize: 13,
    lineHeight: 16,
    triggerPadH: 6,
    triggerPadV: 4,
    moreMinWidth: 22,
    moreHeight: 22,
    moreFontSize: 10,
    popoverOffset: 34,
  },
  large: {
    emojiSize: 18,
    lineHeight: 22,
    triggerPadH: 8,
    triggerPadV: 6,
    moreMinWidth: 26,
    moreHeight: 26,
    moreFontSize: 11,
    popoverOffset: 8,
  },
};

function CollapsedEmoji({ emoji, label, emojiSize, lineHeight }) {
  const tooltip = interestTooltipLabel(label);
  const glyphs = splitInterestEmojiGlyphs(emoji);

  if (Platform.OS === 'web') {
    return (
      <span title={tooltip} aria-label={tooltip} style={webEmojiGroupStyle}>
        {glyphs.map((glyph, idx) => (
          <span
            key={`${glyph}-${idx}`}
            style={{
              ...webGlyphStyle,
              fontSize: emojiSize,
              lineHeight: `${lineHeight}px`,
            }}
          >
            {glyph}
          </span>
        ))}
      </span>
    );
  }

  return (
    <View style={styles.emojiGroup} accessibilityLabel={tooltip}>
      {glyphs.map((glyph, idx) => (
        <Text
          key={`${glyph}-${idx}`}
          style={[styles.emojiGlyph, { fontSize: emojiSize, lineHeight }]}
        >
          {glyph}
        </Text>
      ))}
    </View>
  );
}

/**
 * Compact interest cluster for profile / community cards.
 * Shows a few emoji badges + overflow count; tap to expand full labeled list.
 */
export function CardInterestsDisplay({
  items = [],
  emojis = [],
  maxVisible = DEFAULT_MAX_VISIBLE,
  style,
  size = 'default',
  popoverPlacement = 'below',
  popoverAlign = 'end',
  variant = 'compact',
  theme = null,
}) {
  const [open, setOpen] = useState(false);
  const preset = SIZE_PRESETS[size] || SIZE_PRESETS.default;

  const list = items?.length
    ? items
    : (Array.isArray(emojis) ? emojis : [])
        .filter(Boolean)
        .map((emoji) => ({ emoji, label: emoji }));

  const toggle = useCallback((e) => {
    if (e?.stopPropagation) e.stopPropagation();
    setOpen((prev) => !prev);
  }, []);

  if (!list.length) return null;

  if (variant === 'inline') {
    const chipBg = theme?.background?.secondary || 'rgba(255,255,255,0.08)';
    const chipBorder = theme?.ui?.border || 'rgba(255,255,255,0.12)';
    const labelColor = theme?.text?.secondary || 'rgba(255,255,255,0.75)';
    return (
      <View style={[styles.inlineWrap, style]}>
        {list.map(({ emoji, label }, idx) => {
          const text = interestTooltipLabel(label);
          return (
            <View
              key={`${emoji}-${text}-${idx}`}
              style={[styles.inlineChip, { backgroundColor: chipBg, borderColor: chipBorder }]}
              accessibilityLabel={text}
            >
              <CollapsedEmoji
                emoji={emoji}
                label={label}
                emojiSize={preset.emojiSize}
                lineHeight={preset.lineHeight}
              />
              {text ? (
                <Text style={[styles.inlineLabel, { color: labelColor }]} numberOfLines={2}>
                  {text}
                </Text>
              ) : null}
            </View>
          );
        })}
      </View>
    );
  }

  const visible = list.slice(0, maxVisible);
  const overflow = Math.max(0, list.length - visible.length);

  const popoverPositionStyle =
    popoverPlacement === 'above'
      ? { top: undefined, bottom: '100%', marginBottom: preset.popoverOffset }
      : { top: preset.popoverOffset, bottom: undefined };

  const popoverAlignStyle =
    popoverAlign === 'center'
      ? Platform.OS === 'web'
        ? { left: '50%', right: undefined, transform: 'translateX(-50%)' }
        : { alignSelf: 'center', left: undefined, right: undefined }
      : popoverAlign === 'start'
        ? { left: 0, right: undefined }
        : { right: 0, left: undefined };

  return (
    <View
      style={[styles.wrap, open && styles.wrapOpen, style]}
      onStartShouldSetResponder={() => true}
    >
      <TouchableOpacity
        style={[
          styles.trigger,
          open && styles.triggerOpen,
          { paddingHorizontal: preset.triggerPadH, paddingVertical: preset.triggerPadV },
        ]}
        onPress={toggle}
        activeOpacity={0.88}
        accessibilityLabel={`${list.length} interests. Tap to ${open ? 'hide' : 'view'}.`}
        accessibilityRole="button"
      >
        <View style={styles.visibleRow}>
          {visible.map(({ emoji, label }, idx) => (
            <CollapsedEmoji
              key={`${emoji}-${label}-${idx}`}
              emoji={emoji}
              label={label}
              emojiSize={preset.emojiSize}
              lineHeight={preset.lineHeight}
            />
          ))}
          {overflow > 0 ? (
            <View
              style={[
                styles.moreBadge,
                { minWidth: preset.moreMinWidth, height: preset.moreHeight },
              ]}
            >
              <Text style={[styles.moreText, { fontSize: preset.moreFontSize }]}>
                +{overflow}
              </Text>
            </View>
          ) : null}
        </View>
      </TouchableOpacity>

      {open ? (
        <View
          style={[styles.popover, popoverPositionStyle, popoverAlignStyle]}
          {...(Platform.OS === 'web'
            ? { onClick: (e) => e.stopPropagation?.() }
            : {})}
        >
          <Text style={styles.popoverTitle}>Interests</Text>
          {list.map(({ emoji, label }, idx) => {
            const text = interestTooltipLabel(label);
            const glyphs = splitInterestEmojiGlyphs(emoji);
            return (
              <View key={`${emoji}-${text}-${idx}`} style={styles.popoverRow}>
                <View style={styles.popoverEmojiRow}>
                  {glyphs.map((glyph, gIdx) => (
                    <Text key={`${glyph}-${gIdx}`} style={styles.popoverEmoji}>
                      {glyph}
                    </Text>
                  ))}
                </View>
                <Text style={styles.popoverLabel} numberOfLines={2}>
                  {text}
                </Text>
              </View>
            );
          })}
        </View>
      ) : null}
    </View>
  );
}

const webEmojiGroupStyle = {
  display: 'inline-flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: 1,
  cursor: 'pointer',
  userSelect: 'none',
};

const webGlyphStyle = {
  display: 'inline-block',
};

const styles = StyleSheet.create({
  wrap: {
    position: 'relative',
    zIndex: 1,
  },
  wrapOpen: {
    zIndex: 100,
  },
  trigger: {
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.1,
          shadowRadius: 3,
          elevation: 2,
        }),
  },
  triggerOpen: {
    borderColor: '#14b8a6',
    backgroundColor: '#ffffff',
  },
  visibleRow: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: 4,
  },
  emojiGroup: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: 1,
  },
  emojiGlyph: {
    fontSize: 13,
    lineHeight: 16,
  },
  moreBadge: {
    borderRadius: 11,
    backgroundColor: '#14b8a6',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
    flexShrink: 0,
  },
  moreText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#fff',
    fontFamily: 'VastagoGrotesk',
  },
  popover: {
    position: 'absolute',
    zIndex: 120,
    backgroundColor: '#ffffff',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
    paddingVertical: 8,
    paddingHorizontal: 10,
    minWidth: 168,
    maxWidth: 220,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.18,
          shadowRadius: 12,
          elevation: 8,
        }),
  },
  popoverTitle: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#888',
    marginBottom: 6,
    fontFamily: 'VastagoGrotesk',
  },
  popoverRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 5,
    paddingHorizontal: 6,
    borderRadius: 8,
    backgroundColor: '#f8f8f9',
    marginBottom: 4,
  },
  popoverEmojiRow: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: 1,
    minWidth: 20,
  },
  popoverEmoji: {
    fontSize: 14,
    lineHeight: 18,
    textAlign: 'center',
  },
  popoverLabel: {
    flex: 1,
    fontSize: 12,
    fontWeight: '500',
    color: '#333',
    fontFamily: 'VastagoGrotesk',
    lineHeight: 16,
  },
  inlineWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 6,
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  inlineChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    maxWidth: '100%',
  },
  inlineLabel: {
    fontSize: 11,
    fontWeight: '600',
    flexShrink: 1,
  },
});
